// styles/buttonStyles.ts
import { css, type SerializedStyles, type Theme } from '@emotion/react';
import { borderRadius } from '@shared/constants/Styles';
import { type ButtonSize } from '@shared/models/components/ICoreButtonProps';
import { MixinFocusVisible } from '../structure/DefaultStyles';
import { TextSize100, TextSize200, TextSize90 } from '../text/Typography';

export const buttonBaseStyles = css`
    text-align: center;
    border-radius: ${borderRadius.md};
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer !important;
    position: relative;
    padding: 0.25rem 0.5rem;
    gap: 0.5rem;
    text-underline-offset: 0.15rem;
    @media (prefers-reduced-motion: no-preference) {
        transition: all 0.1s ease-out;
        & * {
            transition: ease-out 0.1s;
        }
    }
    transform-origin: center;
    ${MixinFocusVisible}
`;

export const sizeStyles: Record<ButtonSize, SerializedStyles> = {
    xsmall: css`
        ${TextSize90}
        padding: 0 0.5rem;
        min-height: 24px;
        border-radius: ${borderRadius.sm};
        width: 100%;
    `,
    small: css`
        ${TextSize90}
        min-height: 2rem;
        padding: 0 0.625rem;
    `,
    medium: css`
        ${TextSize100}
        width: fit-content;
        max-width: 100%;
        padding: 0.625rem;
    `,
    large: css`
        ${TextSize200}
        width: 100%;
        padding: 0.875rem;
    `,
    quick: css`
        ${TextSize100}
        padding: 0.625rem 0.875rem;
    `,
};

export const aaaSizeStyles: Record<ButtonSize, SerializedStyles> = {
    xsmall: css`
        ${TextSize90}
        min-height: 2.25rem;
        padding: 0 0.5rem;
    `,
    small: css`
        ${TextSize90}
        min-height: 2.75rem;
        min-width: 44px;
        padding: 0 0.75rem;
    `,
    medium: css`
        ${TextSize100}
        min-width: 44px;
        min-height: 44px;
        width: fit-content;
        max-width: 100%;
        padding: 0.75rem;
    `,
    large: css`
        ${TextSize200}
        width: 100%;
        min-width: 44px;
        min-height: 44px;
        padding: 1rem;
    `,
    quick: css`
        ${TextSize100}
        min-width: 44px;
        min-height: 44px;
        padding: 0.75rem 1rem;
    `,
};

export const disabledButtonStyles = ({ theme }: { theme: Theme }) => css`
    cursor: not-allowed;
    color: ${theme.button.disabled.text};
    background: ${theme.button.disabled.background};
    border-color: ${theme.button.disabled.border};
    svg {
        color: ${theme.button.disabled.icon};
    }
    &:hover {
        background: ${theme.button.disabled.hoverBackground};
        color: ${theme.button.disabled.hoverText};
    }
`;

// Disabled treatment for text/link-style variants: muted via the disabled
// token's text color, no filled background or border.
export const disabledTextButtonStyles = ({ theme }: { theme: Theme }) => css`
    cursor: not-allowed;
    opacity: 0.9;
    color: ${theme.button.disabled.text};
    border-color: ${theme.button.disabled.border};
    &:hover {
        color: ${theme.button.disabled.text};
    }
`;
